import React from "react";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { convertToCloudFrontUrl } from "../../helper";

const statusLabel = (post) => {
  if (post.status === 0) return { text: "Draft — not in the app", color: "#F5A524" };
  if (post.isPostVisible === false) return { text: "Hidden in the app", color: "#F5A524" };
  return { text: "Live", color: "#46A758" };
};

const mediaOf = (post) => {
  if (post.mediaType === "guide") return (post.carouselImages || []).map((s) => ({ position: s.position, url: s.image?.uploadedImageUrl }));
  if (post.mediaType === "image" && post.posts?.uploadedImageUrl) return [{ position: 1, url: post.posts.uploadedImageUrl }];
  if (post.mediaType === "video" && post.reels) return [{ position: 1, url: post.reels.thumbnail?.uploadedImageUrl, video: true }];
  return [];
};

const Row = ({ label, children }) => (
  <div className="d-flex gap-3 py-1">
    <span style={{ minWidth: 130, color: "var(--ps-text-muted)" }}>{label}</span>
    <span style={{ wordBreak: "break-word" }}>{children}</span>
  </div>
);

/**
 * What was actually saved, read back from the create's own answer: id, exact
 * title, pillar → sub-pillar, tags (with ids), media count and the media in
 * carousel order, and whether it is live. So checking a publish no longer
 * needs a second trip to the list.
 */
const PublishResult = ({ post, duplicate, onClose }) => {
  if (!post) return null;
  const status = statusLabel(post);
  const media = mediaOf(post);
  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(post._id);
      toast.success("Id copied.");
    } catch {
      toast.error("Could not copy — select the id instead.");
    }
  };
  return (
    <Modal isOpen onRequestClose={onClose} className="modal_body_lg" overlayClassName="modal_overlay">
      <div className="box_wrapper" data-testid="publish-result" data-record-id={post._id}>
        <h4 className="mb-1">{duplicate ? "Already published" : "Published"}</h4>
        <p className="text-muted mb-3">
          {duplicate
            ? "This was already saved by an earlier Submit — nothing new was created. Here is that record."
            : "Here is exactly what was saved."}
        </p>
        <Row label="Record id">
          <code>{post._id}</code>{" "}
          <button type="button" className="btn btn-sm btn-outline-secondary ms-2" onClick={copyId}>Copy</button>
        </Row>
        <Row label="Title">{post.title || <em className="text-muted">(no title)</em>}</Row>
        <Row label="Type">{post.mediaType === "guide" ? "Guide" : post.mediaType === "video" ? "Reel" : "Post"} · {post.type}</Row>
        <Row label="Pillar">
          {post.pillar?.pillarName ? `${post.pillar.pillarName} → ${post.subPillar?.subPillarName || "?"}` : <em className="text-muted">none</em>}
        </Row>
        <Row label="Tags">
          {(post.tags || []).length
            ? post.tags.map((t) => (
              <span key={t.tagId} className="me-3 d-inline-block">
                {t.tagName} <code style={{ fontSize: 11 }}>{t.tagId}</code>
              </span>
            ))
            : <em className="text-muted">none</em>}
        </Row>
        <Row label="Media">{post.mediaCount ?? media.length}</Row>
        <Row label="Status"><strong style={{ color: status.color }}>{status.text}</strong></Row>
        {media.length > 0 && (
          <div className="d-flex flex-wrap gap-2 mt-3">
            {media.map((m) => (
              <figure key={`${m.position}-${m.url}`} className="m-0 text-center" style={{ width: 120 }}>
                {m.url
                  ? <img src={convertToCloudFrontUrl(m.url)} alt={`Slide ${m.position}`} style={{ width: 120, height: 150, objectFit: "contain", background: "var(--ps-surface-2)", borderRadius: 6 }} />
                  : <div style={{ width: 120, height: 150, background: "var(--ps-surface-2)", borderRadius: 6 }} />}
                <figcaption style={{ fontSize: 12, color: "var(--ps-text-muted)" }}>{m.video ? "video" : `#${m.position}`}</figcaption>
              </figure>
            ))}
          </div>
        )}
        <div className="d-flex justify-content-end mt-3">
          <button type="button" className="btn btn-dark" onClick={onClose}>Done</button>
        </div>
      </div>
    </Modal>
  );
};

export default PublishResult;
